/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties} from "react";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {AlertsModule, OnAlertsUpdated} from "@modules/AlertsModule";
import {AlertStatus, DB_Alert} from "@app/app-shared/alert";
import {COLORS} from "@res/colors";
import {filterInstances} from "@intuitionrobotics/ts-common";
import {hideTooltip} from "../widget/appTooltip";
import {capitalize, DB_User, getUserNameWithTitle} from "@app/ir-q-app-common/types/care-center/user";
import {OnUsersUpdated, UsersModule} from "@modules/UsersModule";
import {Loader} from "../../../widgets/Loader";
import {ClearFilter} from "../../utils/ClearFilter";
import {formatStatus} from "../ui/alerts/util";
import {SelectOrganization} from "../widget/SelectOrganization";
import {AlertTable} from "../ui/alerts/AlertTable";
import {OnWindowResize} from "../../App";
import {PatientContextProvider, PatientsContextType} from "../ui/patients/PatientsContext";
import {ID_ALL, OrgRoleLevel} from "@app/app-shared/permission";
import {arrayToMap} from "@intuitionrobotics/ts-common/utils/array-tools";
import {DB_Patient} from "@app/app-shared/patient";
import {MultiSelectDropDown, MultiSelectOption} from "../../../widgets/MultiSelectDropdown";

export type AlertsTableRow = {
    created?: DB_Alert<any>,
    lastUpdated?: DB_Alert<any>,
    alert?: DB_Alert<any>,
    status?: DB_Alert<any>,
    // Because we cannot retrieve the patient back from the careManager to check for not viewed alerts,
    // we will be using an extra variable to define whether the care manager name should be bold.
    careManager: { careManager?: DB_User, bold?: boolean, alert?: DB_Alert<any>, organizationId?: string }
}

type State = {
    search: string,
    page: number
    customersFilter: Set<string>
    notificationFilter: Set<string>
    statusFilter: Set<AlertStatus | string>
    careManagerFilter: Set<string>
    orgFilter: Set<string>
}

export const tableCardStyle: CSSProperties = {
    backgroundColor: "white",
    boxShadow: "0 0 3px 1px #ececec",
    borderRadius: "6px",
    padding: 16,
    margin: 16,
    marginTop: 0
};

export class AlertsTab
    extends BaseComponent<{}, State>
    implements OnAlertsUpdated,
        OnUsersUpdated,
        OnWindowResize {

    __onUsersUpdated = () => this.forceUpdate();
    __onAlertsUpdated = () => this.forceUpdate();
    __onWindowResize = () => this.forceUpdate();

    static readonly CARE_TEAM_CONTACT = "Requested to be Contacted";
    static readonly METROPLUS_QUESTIONNAIRE = "Metroplus Questionnaire";
    static reverseAlertCategoryMap = new Map<string, string>([
        ["doctor_visit", this.METROPLUS_QUESTIONNAIRE],
        ["doctor_cost", this.METROPLUS_QUESTIONNAIRE],
        ["daily_assistance1", this.METROPLUS_QUESTIONNAIRE],
        ["health_decline", this.METROPLUS_QUESTIONNAIRE],
        ["food_supply", this.METROPLUS_QUESTIONNAIRE],
        ["contact_request", this.METROPLUS_QUESTIONNAIRE],
        ["daily_assistance2", this.METROPLUS_QUESTIONNAIRE],
        ["fall_reported", this.METROPLUS_QUESTIONNAIRE],
        ["medication_amount", this.METROPLUS_QUESTIONNAIRE],
        ["transportation_assistance", this.METROPLUS_QUESTIONNAIRE],
        ["appointment_reminder_alert", "Doctor Appointment"],
        ["medication_alert", "Medication Reminder"],
        ["health_measurement_alert", "Health Data"],
        ["cc", "Health Data"],
        ["contact_me_alert", this.CARE_TEAM_CONTACT],
        ["do_not_contact_me_alert", this.CARE_TEAM_CONTACT],
        ["user_accepted_privacy_plan", "User Joined"]
    ]);



    constructor(props: {}) {
        super(props);
        this.state = {
            search: "",
            page: 1,
            customersFilter: new Set(),
            notificationFilter: new Set(),
            statusFilter: new Set(),
            careManagerFilter: new Set(),
            orgFilter: new Set()
        };
    }

    componentWillUnmount() {
        window.removeEventListener("resize", () => this.forceUpdate());
        hideTooltip();
    }

    componentDidMount() {
        window.addEventListener("resize", () => {
            this.forceUpdate();
        });
    }

    private renderDropdowns = (options: MultiSelectOption<any>[], stateKey: "customersFilter" | "notificationFilter" | "statusFilter" | "careManagerFilter") => {
        return <MultiSelectDropDown
            labels={{main: capitalize(stateKey.replace("Filter", "").replace(/(?<!^)([A-Z])/g, ' $1'))}}
            options={options}
            onItemClick={(value, checked) => {
                if (value === ID_ALL)
                    return this.setState(s => ({...s, [stateKey]: new Set()}));

                this.setState(s => {
                    const set = s[stateKey];
                    if (checked)
                        set.add(value);
                    else
                        set.delete(value);
                    return {...s, [stateKey]: set};
                })
            }}
        />
    };


    private getNotificationsOptions = (alerts?: DB_Alert<any>[]): MultiSelectOption<AlertStatus | string>[] => {
        if (!alerts) return [];

        const alertFilters = alerts.reduce((carry: Set<string>, alert: DB_Alert<any>) => {
            const category = AlertsTab.reverseAlertCategoryMap.get(alert.alertType);
            // Check if category is not undefined before adding it to the Set
            if (category) {
                carry.add(category);  // Add category if it exists
            }
            return carry;
        }, new Set());

        return [{
            label: "All",
            value: ID_ALL,
            checked: this.state.notificationFilter.size === 0
        }, ...[...alertFilters]
            .sort((u1, u2) => u1.localeCompare(u2))
            .map((p) => ({
                label: p,
                value: p,
                checked: this.state.notificationFilter.has(p)
            }))];
    };


    separator = () => {
        return <div style={{
            backgroundColor: COLORS.neutral_gray_2,
            width: "1px",
            height: "20px",
            paddingTop: "5px",
            margin: "10px"
        }}/>;
    };

    renderPatientAlertCount = (content: AlertsTableRow[]) => {
        const uniquePatients = new Set<string>()
        content.forEach(_content => {
            if (_content.alert?.patientId)
                uniquePatients.add(_content.alert.patientId)
        });
        return <div style={{
            flex: 1,
            color: COLORS.neutral_text_dark,
            fontSize: 14,
            fontWeight: 500
        }}>
            {`${uniquePatients.size} patients with notifications`}
        </div>;
    };

    private getPatientsDropdownOptions(patients: DB_Patient[]) {
        return [{
            label: "All",
            value: ID_ALL,
            checked: this.state.customersFilter.size === 0
        }, ...patients
            .sort((u1, u2) => u1.name.localeCompare(u2.name))
            .map((p) => ({
                label: p.name,
                value: p._id,
                checked: this.state.customersFilter.has(p._id)
            }))];
    }

    private getCareManagerFilter(
        patients: DB_Patient[],
        userMap: { [p: string]: DB_User }
    ): MultiSelectOption[] {
        const counterMap = new Map<DB_User, number>();
        patients.forEach(p => {
            const patientEl = p.careManagerId;
            if (!patientEl)
                return;
            const user = userMap[patientEl];
            if (user)
                counterMap.set(user, (counterMap.get(user) || 0) + 1);
        });

        return [{
            label: "All",
            value: ID_ALL,
            checked: this.state.careManagerFilter.size === 0
        }, ...[...counterMap.entries()]
            .sort(([u1], [u2]) => getUserNameWithTitle(u1).localeCompare(getUserNameWithTitle(u2)))
            .map(([user, count]) => ({
                label: getUserNameWithTitle(user),
                value: user._id,
                checked: this.state.careManagerFilter.has(user._id),
                count
            }))];
    }


    private renderHead = (content: AlertsTableRow[], patients: DB_Patient[], alerts?: DB_Alert<any>[]) => {
        const users = UsersModule.getUsers();
        const userMap = arrayToMap(users, user => user._id);

        const patientsDropdownOptions = this.getPatientsDropdownOptions(patients);
        const careManagerDropdownOptions = this.getCareManagerFilter(patients, userMap);
        const notificationFilterOptions = this.getNotificationsOptions(alerts);
        const statusesOptions = this.getStatusesOptions();
        return <div className={`ll_h_c`}>
            <SelectOrganization
                onOrgClicked={(orgId) => {
                    if (orgId === ID_ALL)
                        return this.setState(s => ({...s, orgFilter: new Set()}));
                    this.setState(s => {
                        const orgs = s.orgFilter;
                        if (orgs.has(orgId))
                            orgs.delete(orgId);
                        else
                            orgs.add(orgId);
                        return {...s, orgFilter: orgs};
                    });
                }}
                selected={this.state.orgFilter}
            />
            {this.renderDropdowns(patientsDropdownOptions, "customersFilter")}
            {this.renderDropdowns(notificationFilterOptions, "notificationFilter")}
            {this.renderDropdowns(statusesOptions, "statusFilter")}
            {this.renderDropdowns(careManagerDropdownOptions, "careManagerFilter")}
            <ClearFilter onClick={() => {
                this.setState({
                    search: "",
                    page: 1,
                    customersFilter: new Set(),
                    notificationFilter: new Set(),
                    statusFilter: new Set(),
                    careManagerFilter: new Set(),
                    orgFilter: new Set()
                });
            }}/>
            {this.renderPatientAlertCount(content)}
        </div>;
    }

    private getContent = (patientsContext: PatientsContextType, alerts?: DB_Alert<any>[]): AlertsTableRow[] => {
        const {patients: patientsMap, patientIds} = patientsContext;
        if (!patientIds || !alerts)
            return [];

        const usersMap = arrayToMap(UsersModule.getUsers(), u => u._id);

        return alerts.reduce((acc: AlertsTableRow[], _alert) => {
            const patient = _alert.patientId ? patientsMap[_alert.patientId] : undefined;
            // Org-level alerts (no patientId) are still surfaced — render with no patient/careManager.
            if (_alert.patientId && !patient)
                return acc;
            const careManager = patient?.careManagerId ? usersMap[patient.careManagerId] : undefined;
            const orgId = patient?.organizationId || _alert.organizationId;
            if (!this.calculateFilter(_alert, patient, careManager))
                return acc;
            return [...acc, {
                created: _alert,
                lastUpdated: _alert,
                alert: _alert,
                careManager: careManager ? {careManager, alert: _alert, organizationId: orgId} : {alert: _alert, organizationId: orgId},
                status: _alert
            }]
        }, []);
    };

    private calculateFilter = (alert: DB_Alert<any>, patient?: DB_Patient, careManager?: DB_User) => {
        const filterCareManager = this.state.careManagerFilter.size === 0 || (careManager && this.state.careManagerFilter.has(careManager._id));
        const filterPatients = this.state.customersFilter.size === 0 || (!!patient && this.state.customersFilter.has(patient._id));
        const alertCategory = AlertsTab.reverseAlertCategoryMap.get(alert.alertType);
        const notificationFilter = this.state.notificationFilter.size === 0 || (alertCategory && this.state.notificationFilter.has(alertCategory));
        const statusFilter = this.state.statusFilter.size === 0 || this.state.statusFilter.has(alert.status);
        const orgIdForFilter = patient?.organizationId || alert.organizationId;
        const filterOrg = this.state.orgFilter.size === 0 || (!!orgIdForFilter && this.state.orgFilter.has(orgIdForFilter));
        return filterCareManager && filterPatients && filterOrg && notificationFilter && statusFilter;
    };

    render() {
        return <PatientContextProvider roles={[OrgRoleLevel.USER_DATA]}>{
            (pctx) => {
                const {patientIds, patients: pMap} = pctx;
                const patients = filterInstances(patientIds.map(_id => pMap[_id]));
                const alerts = AlertsModule.getAlerts(pctx.patients);
                const loading = !alerts || !patientIds.length;
                const content = this.getContent(pctx, alerts);
                return <div className={"fill page_table_wrapper"}>
                    {this.renderHead(content, patients, alerts)}
                    {loading ? <Loader/> : <AlertTable
                        content={content}
                        patientsContext={pctx}
                    />}
                </div>
            }
        }</PatientContextProvider>
    }

    private getStatusesOptions = (): MultiSelectOption[] => {
        return [
            AlertStatus.NEW,
            AlertStatus.OPEN,
            AlertStatus.IN_PROGRESS,
            AlertStatus.COMPLETE
        ].reduce((carry: MultiSelectOption[], status) => {
            carry.push({
                label: formatStatus(status),
                value: status,
                checked: this.state.statusFilter.has(status)
            });
            return carry;
        }, [{
            label: "All",
            value: ID_ALL,
            checked: this.state.statusFilter.size === 0
        }]);
    };
}

